import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/shadcn/radio-group';

import { isLockedWidget } from '@/components/forms/utils/links';
import { useDynamicProps } from '@/hooks/useDynamicProps';
import { usePropsSynchronization } from '@/hooks/usePropsSynchronization';
import { TestId } from '@/types/testId';
import type { WidgetWriterProps } from '../widget.type';

type Option<T = string> = {
  label?: string;
  value: T;
};

type RadioOptions = Option[] | [Option<true>, Option<false>];

type Props = {
  options: {
    enumOptions: RadioOptions;
    enumDisabled: string[];
  };
  enum?: RadioOptions;
  enumDisabled?: string[];
} & WidgetWriterProps;

export const Radio = (rjsfProps: Props) => {
  const props = useDynamicProps(rjsfProps);
  usePropsSynchronization(props, rjsfProps);

  const enumDisabled = props.enumDisabled || props.options?.enumDisabled;

  const options = props.enum || props.options.enumOptions;
  // check if the widget is locked because of forms links
  const isLocked = isLockedWidget(props.id, props.formContext.links);

  const disabledGroup = props.disabled || props.readonly || isLocked;
  if (options.length < 2) {
    console.error(
      'Minimum 2 options should be added to radio buttons within the JSONSchema'
    );
  }

  return (
    <RadioGroup
      className="gap-1"
      data-cy={`radio-${props.name}`}
      data-testid={TestId.FormWriter.Radio.RadioGroup}
      disabled={disabledGroup}
      id={props.id}
      onValueChange={value => props.onChange(value)}
      value={props.value || ''}
    >
      {options.map(option => {
        const disabledOption =
          enumDisabled && enumDisabled.indexOf(option.value) !== -1;
        const uniqueKey = `${props.name}-${option.value}`;

        const isDisabled = disabledGroup || disabledOption;
        return (
          <div
            className={cn(
              'flex h-7 cursor-pointer flex-row items-center text-foreground/80',
              !isDisabled && 'hover:text-foreground'
            )}
            key={uniqueKey}
            title={isDisabled ? 'This option is disabled' : undefined}
          >
            <RadioGroupItem
              aria-invalid={props.invalid}
              checked={option.value === props.value}
              data-cy={`radio-${uniqueKey}`}
              data-testid={TestId.FormWriter.Radio.RadioItem}
              disabled={isDisabled}
              id={uniqueKey}
              value={option.value}
            />
            <Label
              aria-disabled={isDisabled}
              className={cn(
                'flex h-full grow items-center truncate pt-0.5 pl-2 font-medium',
                isDisabled
                  ? 'cursor-not-allowed text-current/50'
                  : 'cursor-pointer'
              )}
              htmlFor={uniqueKey}
            >
              {option.label}
            </Label>
          </div>
        );
      })}
    </RadioGroup>
  );
};
